import type SSOError from 'bundles/authentication-common/components/sso/SSOError';
import { InvalidDomainSSOError } from 'bundles/authentication-common/components/sso/SSOError';
import thirdPartyAuth, { SSOTypes } from 'bundles/third-party-auth/lib';

export const SSO_VARIANT = {
  SOCIAL: 'social',
  CUSTOM: 'custom',
  NONE: 'none',
} as const;

export type SSO_VARIANT_KEY = (typeof SSO_VARIANT)[keyof typeof SSO_VARIANT];

export function getSSOVariant({
  authProvider,
  isEnterprise,
}: {
  authProvider?: string;
  isEnterprise?: boolean;
}): SSO_VARIANT_KEY {
  if (authProvider) {
    return SSO_VARIANT.CUSTOM;
  }
  if (isEnterprise) {
    return SSO_VARIANT.NONE;
  }
  return SSO_VARIANT.SOCIAL;
}

// Only LTI's invalid-domain redirect should be completed via thirdPartyAuth.connect.
// SAML's DOMAIN_VERIFICATION_FAILED case is handled separately via its magic-link resend flow.
export const isLtiInvalidDomainSSOError = (error?: SSOError): error is InvalidDomainSSOError =>
  error instanceof InvalidDomainSSOError && error.method?.toLowerCase() === SSOTypes.LTI;

// Social login (Google/Facebook/Apple) has no awareness of a pending LTI domain-verification-failed
// launch, so a successful social login would otherwise leave the LTI launch disconnected (orphaned
// invitation, no grade sync, no private-course auto-join). Fire the same completion call the
// password/signup flows already make once their session is established (web#66998); the account is
// already authenticated via OAuth, so no email/password is sent, only the pending LTI token.
// If the completion call fails, the learner is already logged in via social login, so we let them
// continue with the normal consumer login/signup flow rather than surfacing an error for a launch
// they didn't initiate this way.
export const completeLtiLaunchIfPending = async (ssoError: SSOError | undefined): Promise<void> => {
  if (!isLtiInvalidDomainSSOError(ssoError) || !ssoError.token) {
    return;
  }

  try {
    await thirdPartyAuth.connect(SSOTypes.LTI, { token: ssoError.token, forceThirdPartyLink: true });
  } catch {
    // Intentionally swallowed — see comment above.
  }
};
